import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchActiveCountries } from '@/shared/api/places';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { toOptions } from '@/shared/lib/options';
import { getTotalPages } from '@/shared/lib/pagination';
import { useListFilters } from '@/shared/lib/useListFilters';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchBasicAnalytics } from './api';
import { ANALYTICS_SECTIONS, type AnalyticsSection } from './analyticsSections';

type Stats = Record<string, number | string | null | undefined>;
type DailyStats = Stats & { create_time: number | string };

interface AnalyticsResponse {
  statistic?: { every_day?: DailyStats[]; month?: Stats };
}

const DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';
const RUSSIA_ID = '1';

// The API only has data up to yesterday, and the page is limited to the current month (as in legacy).
const yesterday = () => dayjs().subtract(1, 'day');

const getEmptyFilters = () => ({
  dateFrom: yesterday().startOf('month').format(DATE_FORMAT),
  dateTo: yesterday().format(DATE_FORMAT),
  countryId: RUSSIA_ID,
});

const AnalyticsTable = ({
  section,
  days,
  month,
}: {
  section: AnalyticsSection;
  days: DailyStats[];
  month: Stats;
}) => (
  <div className="box-body">
    <h4 className="box-title">{section.title}</h4>
    <table className="table table-bordered">
      <thead>
        <tr>
          <th>Дата</th>
          {section.columns.map(({ header }) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {days.map((day) => (
          <tr key={day.create_time}>
            <td>{dayjs.unix(Number(day.create_time)).format(DISPLAY_DATE_FORMAT)}</td>
            {section.columns.map(({ field }) => (
              <td key={field}>{day[field] || 0}</td>
            ))}
          </tr>
        ))}
        <tr>
          <td>
            <strong>С начала месяца</strong>
          </td>
          {section.columns.map(({ field }) => (
            <td key={field}>{month[field] || 0}</td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

export const StatAnalyticsPage = () => {
  const list = useListFilters(getEmptyFilters());
  const { draft, applied, page } = list;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['stat-analytics', applied, page],
    queryFn: () =>
      fetchBasicAnalytics({
        page,
        create_time_from: dateInputToUnix(applied.dateFrom),
        create_time_to: dateInputToUnix(applied.dateTo, 'end'),
        country_id: applied.countryId,
      }) as Promise<AnalyticsResponse>,
  });
  const countries = useQuery({ queryKey: ['stat-analytics-countries'], queryFn: fetchActiveCountries });

  const days = data?.statistic?.every_day ?? [];
  const month = data?.statistic?.month ?? {};
  const minDate = yesterday().startOf('month').format(DATE_FORMAT);
  const maxDate = yesterday().format(DATE_FORMAT);

  return (
    <>
      <section className="content-header">
        <h1>Статистика</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Базовая аналитика</h3>
            <form onSubmit={list.submit} style={{ marginTop: 12 }}>
              <FilterField label="От даты">
                <DateTimeInput
                  type="date"
                  value={draft.dateFrom}
                  min={minDate}
                  max={maxDate}
                  onChange={(value) => list.setField('dateFrom', value)}
                />
              </FilterField>
              <FilterField label="До даты">
                <DateTimeInput
                  type="date"
                  value={draft.dateTo}
                  min={minDate}
                  max={maxDate}
                  onChange={(value) => list.setField('dateTo', value)}
                />
              </FilterField>
              <FilterField label="Страна">
                <FilterSelect
                  options={toOptions(countries.data)}
                  value={draft.countryId}
                  isLoading={countries.isLoading}
                  placeholder="Выберите страну..."
                  onChange={(value) => list.setField('countryId', value)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки аналитики" />
          {data &&
            ANALYTICS_SECTIONS.map((section) => (
              <AnalyticsTable key={section.title} section={section} days={days} month={month} />
            ))}
          <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
        </div>
      </section>
    </>
  );
};
